import type { DesignThemeTokens } from './types';

/**
 * Dark theme tokens aligned with Apple system colors (iOS/macOS).
 * @see https://developer.apple.com/design/human-interface-guidelines/color
 */
export const appleDarkTokens: DesignThemeTokens = {
  color: {
    background: {
      primary: '#000000',
      secondary: '#1C1C1E',
      tertiary: '#2C2C2E',
      inverse: '#F2F2F7',
    },
    surface: {
      base: '#1C1C1E',
      elevated: '#2C2C2E',
      grouped: '#1C1C1E',
      chrome: 'rgba(22, 22, 24, 0.38)',
      modal: 'rgba(28, 28, 30, 0.62)',
      overlay: 'rgba(0, 0, 0, 0.5)',
      overlayModal: 'rgba(0, 0, 0, 0.32)',
    },
    border: {
      hairline: 'rgba(84, 84, 88, 0.36)',
      primary: 'rgba(84, 84, 88, 0.65)',
      secondary: '#38383A',
      opaque: '#38383A',
    },
    text: {
      primary: '#FFFFFF',
      secondary: 'rgba(235, 235, 245, 0.6)',
      tertiary: 'rgba(235, 235, 245, 0.3)',
      quaternary: 'rgba(235, 235, 245, 0.18)',
      inverse: '#000000',
      onAccent: '#FFFFFF',
    },
    fill: {
      primary: 'rgba(120, 120, 128, 0.36)',
      secondary: 'rgba(120, 120, 128, 0.32)',
      tertiary: 'rgba(118, 118, 128, 0.24)',
      quaternary: 'rgba(116, 116, 128, 0.18)',
    },
    link: {
      default: '#0A84FF',
      hover: '#409CFF',
    },
    semantic: {
      success: '#30D158',
      successMuted: 'rgba(48, 209, 88, 0.2)',
      warning: '#FF9F0A',
      warningMuted: 'rgba(255, 159, 10, 0.2)',
      error: '#FF453A',
      errorMuted: 'rgba(255, 69, 58, 0.2)',
      info: '#0A84FF',
      infoMuted: 'rgba(10, 132, 255, 0.2)',
    },
    control: {
      primaryBg: 'rgba(255, 122, 82, 0.32)',
      primaryText: '#FFFFFF',
      secondaryBg: 'rgba(120, 120, 128, 0.24)',
      secondaryText: '#EBEBF5',
      secondaryBorder: 'rgba(84, 84, 88, 0.36)',
      outlineBg: 'transparent',
      outlineText: '#FF7A52',
      outlineBorder: '#FF7A52',
    },
    input: {
      bg: 'rgba(120, 120, 128, 0.24)',
      border: 'rgba(84, 84, 88, 0.65)',
      borderFocus: '#FF7A52',
      text: '#FFFFFF',
      placeholder: 'rgba(235, 235, 245, 0.3)',
    },
    icon: {
      primary: 'rgba(235, 235, 245, 0.6)',
      secondary: 'rgba(235, 235, 245, 0.3)',
      inverse: '#000000',
    },
    interaction: {
      hoverPrimary: 'rgba(255, 122, 82, 0.16)',
      hoverSecondary: 'rgba(120, 120, 128, 0.32)',
      focusRing: 'rgba(10, 132, 255, 0.45)',
      selection: 'rgba(10, 132, 255, 0.28)',
    },
  },
  blur: {
    chrome: '10px',
    chromeSaturate: '180%',
    overlay: '4px',
  },
  radius: {
    control: '8px',
    panel: '12px',
    sheet: '16px',
    pill: '999px',
  },
  spacing: {
    xs: '4px',
    sm: '8px',
    md: '12px',
    lg: '16px',
    xl: '24px',
    xxl: '32px',
  },
  typography: {
    titleLg: '32px',
    titleMd: '22px',
    titleSm: '17px',
    body: '15px',
    callout: '14px',
    caption: '12px',
    weightRegular: '400',
    weightMedium: '500',
    weightSemibold: '600',
  },
  motion: {
    fast: '150ms',
    normal: '250ms',
    panel: '320ms',
    easingStandard: 'cubic-bezier(0.25, 0.1, 0.25, 1)',
    easingEmphasized: 'cubic-bezier(0.2, 0, 0, 1)',
  },
  shadow: {
    sm: '0 1px 3px rgba(0, 0, 0, 0.35)',
    md: '0 4px 16px rgba(0, 0, 0, 0.45)',
    lg: '0 12px 32px rgba(0, 0, 0, 0.55)',
  },
};
